/**
 * Scheduler Planner - Main Entry Point
 *
 * Planner workbench for:
 * - Priority management (drag-and-drop reorder)
 * - Resource allocation
 * - Exception handling
 * - Schedule visualization
 *
 * See DESIGN-SYSTEM.md for UI standards.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AppLayout, ErrorBoundary } from './components';
import { DashboardPage, OrdersPage, OrderDetailPage, SchedulePage, ImportsPage, AlertsPage, PeoplePage, SerializedUnitsPage, SettingsPage, NotFoundPage, InventoryPage } from './pages';

import './index.css';

// Configure React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppLayout>
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:orderId" element={<OrderDetailPage />} />
              <Route path="/schedule" element={<SchedulePage />} />
              {/* People and Alerts pages */}
              <Route path="/people" element={<PeoplePage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/imports" element={<ImportsPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/serialized" element={<SerializedUnitsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              {/* Catch-all for 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AppLayout>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Placeholder component for unimplemented pages
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
      <div className="rounded-lg border border-dashed border-border bg-background p-8 text-center">
        <p className="text-foreground-muted">
          This page will be implemented in a future phase.
        </p>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
